/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.thresholdLow = parseInt(this.dataset.thresholdLow, 10) || 0;
      this.thresholdVeryLow = parseInt(this.dataset.thresholdVeryLow, 10) || 0;
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
        this.updateLabelText();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      // If the shopper's new pick doesn't form a real variant with the other
      // currently selected options (e.g. an Age option tied 1:1 to a Colour
      // option), snap the other selectors to the closest real, preferably
      // in-stock, variant instead of leaving the picker stuck on a
      // combination that doesn't exist.
      if (!this.variant && evt && evt.target && evt.target.classList.contains('js-option')) {
        const changedSelector = evt.target.closest('.option-selector[data-selector-type="listed"]');
        const changedIndex = changedSelector ? Array.from(this.optionSelectors).indexOf(changedSelector) : -1;
        const bestMatch = changedIndex === -1 ? null : this.findBestMatchingVariant(changedIndex, evt.target.value);

        if (bestMatch) {
          this.optionSelectors.forEach((selector, index) => {
            if (index === changedIndex || selector.dataset.selectorType !== 'listed') return;
            selector.querySelectorAll('.js-option').forEach((option) => {
              option.checked = option.value === bestMatch.options[index];
            });
          });

          this.selectedOptions = this.getSelectedOptions();
          this.variant = bestMatch;
        }
      }

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      this.updateLabelText();

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     * @param {number|null} quantity - Combined inventory quantity of the matching available
     *   variant(s), or null when inventory isn't tracked (treated as unlimited stock).
     */
    updateOptionAvailability(optionEl, exists, available, quantity = null) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');
        let statusText = unavailableText;
        let level = exists ? 'none' : 'nonexistent';

        if (available) {
          if (quantity !== null && quantity <= this.thresholdVeryLow) {
            statusText = theme.strings.veryLowStock;
            level = 'very_low';
          } else if (quantity !== null && quantity <= this.thresholdLow) {
            statusText = theme.strings.lowStock;
            level = 'low';
          } else {
            statusText = theme.strings.inStock;
            level = 'in_stock';
          }
        }

        if (em) {
          em.hidden = false;
          em.textContent = statusText;
          em.dataset.level = level;
        } else {
          el.innerHTML = `${el.innerHTML} <em class="pointer-events-none" data-level="${level}">${statusText}</em>`;
        }

        return level;
      }

      if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }

      return available ? 'in_stock' : (exists ? 'none' : 'nonexistent');
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;

      // Cascading dependency chain: the first option (e.g. Dexterity) is
      // unconditional. Each option after that is only considered available
      // for values that pair with the CURRENTLY SELECTED value of every
      // option before it - e.g. Age is filtered by the selected Dexterity,
      // and Colour is filtered by both the selected Dexterity AND the
      // selected Age. An earlier option that hasn't been selected yet is
      // permissive (doesn't filter anything below it). This keeps the
      // top-down flow (pick Dexterity, then Age, then Colour) consistent
      // regardless of which availabilityMethod a given block uses.
      this.optionSelectors.forEach((selector, selectorIndex) => {
        const options = selector.querySelectorAll('.js-option:not([data-value=""])');
        const results = [];

        options.forEach((optionEl) => {
          const optionValue = selector.dataset.selectorType === 'dropdown'
            ? optionEl.dataset.value
            : optionEl.value;

          let exists = false;
          let available = false;
          let tracked = false;
          let quantity = 0;

          this.data.product.variants.forEach((variant) => {
            if (variant.options[selectorIndex] !== optionValue) return;

            for (let i = 0; i < selectorIndex; i += 1) {
              if (this.selectedOptions[i] !== null && variant.options[i] !== this.selectedOptions[i]) return;
            }

            exists = true;
            if (variant.available) {
              available = true;
              if (variant.inventory_management) {
                tracked = true;
                quantity += Math.max(variant.inventory_quantity, 0);
              }
            }
          });

          const level = this.updateOptionAvailability(optionEl, exists, available, tracked ? quantity : null);
          results.push({ el: optionEl, level });
        });

        if (selector.dataset.selectorType === 'dropdown') {
          VariantPicker.sortDropdownOptions(selector, results);
        }
      });
    }

    /**
     * Reorders a dropdown's option list so available options surface first
     * and unavailable/sold-out options sink to the bottom, so shoppers don't
     * have to scroll past out-of-stock values to find what's in stock.
     * @param {Element} selector - The '.option-selector[data-selector-type="dropdown"]' element.
     * @param {Array} results - '{ el, level }' entries, in original DOM order. Level is one of
     *   'in_stock', 'low', 'very_low', 'none' (sold out) or 'nonexistent'.
     */
    static sortDropdownOptions(selector, results) {
      const listbox = selector.querySelector('.custom-select__listbox');
      if (!listbox) return;

      const levelOrder = { in_stock: 0, low: 1, very_low: 2, none: 3, nonexistent: 3 };

      results
        .slice()
        .sort((a, b) => levelOrder[a.level] - levelOrder[b.level])
        .forEach(({ el }) => listbox.appendChild(el));
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the option label text (for all options, not just color).
     */
    updateLabelText() {
      // Update all option selectors based on checked inputs
      const optionSelectors = this.querySelectorAll('.option-selector[data-selector-type="listed"]');
      
      optionSelectors.forEach((selector) => {
        const checkedInput = selector.querySelector('.js-option:checked');
        if (!checkedInput) return;

        // Get the label element
        const label = checkedInput.nextElementSibling;
        if (!label) return;

        // Find the value element (for swatch/image styles)
        const valueElement = label.querySelector('.js-value');
        
        let selectedValue;
        if (valueElement) {
          // Swatch/image style - get from .js-value element
          selectedValue = valueElement.textContent.trim();
        } else {
          // Button style - get from input value attribute
          selectedValue = checkedInput.value;
        }

        // Update color text (legacy support)
        const colorText = selector.querySelector('.js-color-text');
        if (colorText) {
          colorText.textContent = selectedValue;
        }

        // Update option text (for all options)
        const optionText = selector.querySelector('.js-option-text');
        if (optionText) {
          optionText.textContent = selectedValue;
        }
      });
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update price.
        if (variant.compare_at_price > variant.price) {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.salePrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
            priceWasEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
          }
        } else {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = '';
            priceWasEl.querySelector('.js-value').innerHTML = '';
          }
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Finds the closest real variant when the option the shopper just
     * picked doesn't form a valid combination with the other currently
     * selected options (common when options aren't fully independent -
     * e.g. an Age option that's tied 1:1 to a Colour option). Keeps the
     * just-changed option fixed and matches as many of the other selected
     * options as possible, preferring in-stock variants when more than one
     * combination ties on match count.
     * @param {number} changedIndex - Index of the option selector that changed.
     * @param {string} changedValue - The newly selected value at that index.
     * @returns {?object} The best matching variant, or null if none share the changed value.
     */
    findBestMatchingVariant(changedIndex, changedValue) {
      let best = null;
      let bestScore = -1;

      this.data.product.variants.forEach((variant) => {
        if (variant.options[changedIndex] !== changedValue) return;

        let score = 0;
        variant.options.forEach((value, index) => {
          if (index !== changedIndex && value === this.selectedOptions[index]) {
            score += 1;
          }
        });

        const betterScore = score > bestScore;
        const tiedButMoreAvailable = score === bestScore && variant.available && !(best && best.available);

        if (betterScore || tiedButMoreAvailable) {
          best = variant;
          bestScore = score;
        }
      });

      return best;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
